/**
 * Desk Pulse widget catalog — customer-selective rail snapshots.
 *
 * Free desk: 1 slot · Capital only.
 * Operator+: 2 slots · Capital + Book + Agents unlock.
 * Desk+: 3 slots · same catalog (more simultaneous widgets).
 *
 * Never invent balances. Widgets render `—` until data is ready.
 */

import type { PlanTier } from '@/lib/stripe/types'

export type PulseWidgetId = 'capital' | 'book' | 'agents'

export type PulseMinTier = 'free' | 'starter' | 'advanced'

export interface PulseWidgetDef {
  id: PulseWidgetId
  label: string
  description: string
  /** Minimum plan that may place this widget in a slot. */
  minTier: PulseMinTier
  /** Deep-link when the widget body is clicked. */
  href: string
}

export const PULSE_WIDGETS: readonly PulseWidgetDef[] = [
  {
    id: 'capital',
    label: 'Capital',
    description: 'Equity and day P&L snapshot',
    minTier: 'free',
    href: '/client/dashboard',
  },
  {
    id: 'book',
    label: 'Book',
    description: 'Open positions and working orders',
    minTier: 'starter',
    href: '/trading',
  },
  {
    id: 'agents',
    label: 'Agents',
    description: 'Running, paused, and error counts',
    minTier: 'starter',
    href: '/ai-trading',
  },
] as const

const STORAGE_KEY = 'mechagora.pulse.widgets.v1'

const TIER_RANK: Record<PlanTier | 'none', number> = {
  none: 0,
  free: 1,
  starter: 2,
  advanced: 3,
  professional: 4,
  enterprise: 5,
}

const MIN_RANK: Record<PulseMinTier, number> = {
  free: 1,
  starter: 2,
  advanced: 3,
}

/** How many Pulse slots the plan may fill. */
export function pulseSlotCount(tier: PlanTier | 'none'): number {
  if (tier === 'none' || tier === 'free') return 1
  if (tier === 'starter') return 2
  return 3
}

export function canUsePulseWidget(
  widgetId: PulseWidgetId,
  tier: PlanTier | 'none'
): boolean {
  const def = PULSE_WIDGETS.find((w) => w.id === widgetId)
  if (!def) return false
  return TIER_RANK[tier] >= MIN_RANK[def.minTier]
}

export function defaultPulseSelection(tier: PlanTier | 'none'): PulseWidgetId[] {
  const slots = pulseSlotCount(tier)
  const available = PULSE_WIDGETS.filter((w) => canUsePulseWidget(w.id, tier)).map(
    (w) => w.id
  )
  return available.slice(0, slots)
}

export function normalizePulseSelection(
  selected: PulseWidgetId[],
  tier: PlanTier | 'none'
): PulseWidgetId[] {
  const slots = pulseSlotCount(tier)
  const allowed = selected.filter((id) => canUsePulseWidget(id, tier))
  const unique: PulseWidgetId[] = []
  for (const id of allowed) {
    if (!unique.includes(id)) unique.push(id)
  }
  if (unique.length === 0) return defaultPulseSelection(tier)
  return unique.slice(0, slots)
}

export function readPulseSelection(tier: PlanTier | 'none'): PulseWidgetId[] {
  if (typeof window === 'undefined') return defaultPulseSelection(tier)
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return defaultPulseSelection(tier)
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return defaultPulseSelection(tier)
    const ids = parsed.filter(
      (v): v is PulseWidgetId =>
        v === 'capital' || v === 'book' || v === 'agents'
    )
    return normalizePulseSelection(ids, tier)
  } catch {
    return defaultPulseSelection(tier)
  }
}

export function writePulseSelection(ids: PulseWidgetId[]): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids))
  } catch {
    /* ignore quota */
  }
}

export function unlockLabel(minTier: PulseMinTier): string {
  if (minTier === 'free') return 'Free'
  if (minTier === 'starter') return 'Operator+'
  return 'Desk+'
}
